import { Link, useLocation } from 'react-router-dom';
import { BidText } from '../common/BidToken';
import type { TopicDefinition } from '../../config/moduleCatalog';

/**
 * "Study <topic title>" — the right-aligned link on a topic page's title line.
 *
 * It replaces the earlier "Study all chapters" disclosure (feature 023, FR-014 /
 * SC-006), which opened a panel holding the whole chapter list, the notes search,
 * and the bulk downloads. Each topic maps to exactly one chapter, so the link
 * navigates straight to that chapter's reading page. Search and the bulk downloads
 * remain on `/learn`.
 *
 * The label names the TOPIC rather than the chapter number: "Study 1 Club responses"
 * or "Study Chapter 01" both make the reader map a number onto a bid before they know
 * where they are going. The title is rendered through `BidText` so its bid tokens
 * become suit symbols, per the constitution's suit-symbol standard.
 *
 * Shown on BOTH families. Competitive bidding has no chapter set of its own, but its
 * topics are the same nine topics as precision, so a competitive topic studies the
 * same Precision chapter its bid belongs to.
 *
 * Renders nothing when the topic has no chapter (`counting`).
 *
 * `className` is applied to the anchor itself rather than to a wrapper, because the
 * component renders NOTHING for a chapter-less topic: a positioning wrapper in the
 * caller would still take a flex slot and leave a gap under the brass rule.
 */
export function StudyChapterLink({
  topic,
  className = '',
}: {
  topic: TopicDefinition;
  className?: string;
}) {
  const location = useLocation();

  if (!topic.chapter) return null;

  return (
    <Link
      to={`/learn/${topic.chapter.slug}`}
      // Tells the reader where Back should return to. Without it the reader cannot
      // distinguish this entry from the Learn list and would strand the learner on
      // the wrong page. `search` is carried so a filtered topic URL survives the trip.
      state={{ from: `${location.pathname}${location.search}`, fromLabel: topic.title }}
      data-testid="study-chapter"
      className={
        'rounded-md text-[13px] font-bold text-primary-600 underline transition-colors hover:text-primary-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-600 focus-visible:ring-offset-2 ' +
        className
      }
    >
      Study “<BidText text={topic.title} />”
    </Link>
  );
}
